import type { InlineContext, MarkdownConfig } from "@lezer/markdown";
import { Tag, tags } from "@lezer/highlight";

/** 하이라이트 태그: 테마에서 색을 입힌다. */
export const wikiLinkTag = Tag.define(tags.link);
export const mentionTag = Tag.define();
export const taskRefTag = Tag.define();

const CHAR_AT = 64; // @
const CHAR_HASH = 35; // #
const CHAR_OPEN_BRACKET = 91; // [

const WORD_CHAR = /[\p{L}\p{N}_]/u;
const ASCII_WORD_CHAR = /[A-Za-z0-9_]/;

function charBefore(cx: InlineContext, pos: number): string {
  return pos > cx.offset ? String.fromCharCode(cx.char(pos - 1)) : "";
}

/**
 * `[[제목]]` 또는 `[[제목|표시 이름]]`의 안쪽 텍스트를 나눈다.
 */
export function parseWikiTarget(inner: string): { target: string; label: string } {
  const bar = inner.indexOf("|");
  const target = (bar < 0 ? inner : inner.slice(0, bar)).trim();
  const alias = bar < 0 ? "" : inner.slice(bar + 1).trim();
  return { target, label: alias || target };
}

/**
 * JustOne 전용 인라인 문법.
 * - `[[페이지]]`, `[[페이지|별칭]]` → WikiLink
 * - `@이름` → Mention (단어 중간의 @, 예: 이메일은 제외)
 * - `#T-123` → TaskRef
 */
export const justOneSyntax: MarkdownConfig = {
  defineNodes: [
    { name: "WikiLink", style: wikiLinkTag },
    { name: "WikiLinkMark", style: tags.processingInstruction },
    { name: "WikiLinkTarget" },
    { name: "Mention", style: mentionTag },
    { name: "MentionMark" },
    { name: "TaskRef", style: taskRefTag },
  ],
  parseInline: [
    {
      name: "WikiLink",
      before: "Link",
      parse(cx, next, pos) {
        if (next !== CHAR_OPEN_BRACKET || cx.char(pos + 1) !== CHAR_OPEN_BRACKET) return -1;
        const rest = cx.slice(pos + 2, cx.end);
        const close = rest.indexOf("]]");
        if (close <= 0) return -1;
        const inner = rest.slice(0, close);
        if (/[[\]\n]/.test(inner) || !parseWikiTarget(inner).target) return -1;
        const end = pos + 2 + close + 2;
        return cx.addElement(
          cx.elt("WikiLink", pos, end, [
            cx.elt("WikiLinkMark", pos, pos + 2),
            cx.elt("WikiLinkTarget", pos + 2, end - 2),
            cx.elt("WikiLinkMark", end - 2, end),
          ]),
        );
      },
    },
    {
      name: "Mention",
      parse(cx, next, pos) {
        if (next !== CHAR_AT) return -1;
        if (WORD_CHAR.test(charBefore(cx, pos))) return -1;
        const match = /^[\p{L}\p{N}_][\p{L}\p{N}_.-]*/u.exec(cx.slice(pos + 1, cx.end));
        if (!match) return -1;
        const name = match[0].replace(/[.-]+$/, "");
        const end = pos + 1 + name.length;
        return cx.addElement(cx.elt("Mention", pos, end, [cx.elt("MentionMark", pos, pos + 1)]));
      },
    },
    {
      name: "TaskRef",
      parse(cx, next, pos) {
        if (next !== CHAR_HASH) return -1;
        if (WORD_CHAR.test(charBefore(cx, pos))) return -1;
        const match = /^T-\d+/.exec(cx.slice(pos + 1, cx.end));
        if (!match) return -1;
        const end = pos + 1 + match[0].length;
        // "#T-123에서"처럼 한글 조사는 허용하고, "#T-123abc"는 거부한다.
        if (end < cx.end && ASCII_WORD_CHAR.test(String.fromCharCode(cx.char(end)))) return -1;
        return cx.addElement(cx.elt("TaskRef", pos, end));
      },
    },
  ],
};

export const mathTag = Tag.define(tags.monospace);

const CHAR_DOLLAR = 36; // $
const CHAR_BACKSLASH = 92;
const isBlank = (c: number) => c === 32 || c === 9 || c === 10 || c < 0;
const isDigit = (c: number) => c >= 48 && c <= 57;
/** `$$`만 있는 줄 (수식 블록을 열고 닫는다) */
const MATH_FENCE = /^\$\$\s*$/;

/**
 * 수식 (KaTeX로 그린다).
 * - `$x^2$` → InlineMath. 여는 `$` 뒤와 닫는 `$` 앞에 공백이 없어야 하고 닫는 `$` 뒤가 숫자면 안 된다 ("$5와 $10"은 글).
 * - 줄 안의 `$$x$$` → InlineMath (가운데 정렬 수식)
 * - `$$`만 있는 줄로 감싼 블록, 또는 한 줄짜리 `$$ x $$` → MathBlock
 */
export const mathSyntax: MarkdownConfig = {
  defineNodes: [
    { name: "InlineMath", style: mathTag },
    { name: "InlineMathMark", style: tags.processingInstruction },
    { name: "MathBlock", block: true, style: mathTag },
    { name: "MathMark", style: tags.processingInstruction },
  ],
  parseInline: [
    {
      name: "InlineMath",
      parse(cx, next, pos) {
        if (next !== CHAR_DOLLAR) return -1;
        const double = cx.char(pos + 1) === CHAR_DOLLAR;
        const start = pos + (double ? 2 : 1);
        const first = cx.char(start);
        if (isBlank(first) || first === CHAR_DOLLAR) return -1;
        for (let i = start + 1; i < cx.end; i++) {
          const c = cx.char(i);
          if (c === 10) return -1;
          if (c === CHAR_BACKSLASH) {
            i++;
            continue;
          }
          if (c !== CHAR_DOLLAR) continue;
          if (double) {
            if (cx.char(i + 1) !== CHAR_DOLLAR) continue;
            return cx.addElement(cx.elt("InlineMath", pos, i + 2, [cx.elt("InlineMathMark", pos, pos + 2), cx.elt("InlineMathMark", i, i + 2)]));
          }
          if (isBlank(cx.char(i - 1)) || isDigit(cx.char(i + 1))) continue;
          return cx.addElement(cx.elt("InlineMath", pos, i + 1, [cx.elt("InlineMathMark", pos, pos + 1), cx.elt("InlineMathMark", i, i + 1)]));
        }
        return -1;
      },
    },
  ],
  parseBlock: [
    {
      name: "MathBlock",
      before: "FencedCode",
      parse(cx, line) {
        if (line.next !== CHAR_DOLLAR || line.text.charCodeAt(line.pos + 1) !== CHAR_DOLLAR || line.indent - line.baseIndent >= 4) return false;
        const from = cx.lineStart + line.pos;
        const rest = line.text.slice(line.pos + 2).trimEnd();
        // 한 줄짜리 `$$ x $$`
        if (rest.length > 2 && rest.endsWith("$$")) {
          const close = cx.lineStart + line.pos + 2 + rest.length - 2;
          cx.addElement(cx.elt("MathBlock", from, close + 2, [cx.elt("MathMark", from, from + 2), cx.elt("MathMark", close, close + 2)]));
          cx.nextLine();
          return true;
        }
        if (rest.trim()) return false;
        const marks = [cx.elt("MathMark", from, from + 2)];
        let end = -1;
        // 인용·목록 안이면 그 블록이 끝나는 줄에서 멈춘다 (lezer의 FencedCode와 같은 조건. depth는 공개 타입에 없다)
        while (cx.nextLine() && (line as unknown as { depth: number }).depth >= cx.depth) {
          for (const m of line.markers) marks.push(m);
          const t = line.text.slice(line.pos).trimEnd();
          if (t.endsWith("$$")) {
            const close = cx.lineStart + line.pos + t.length - 2;
            marks.push(cx.elt("MathMark", close, close + 2));
            end = close + 2;
            cx.nextLine();
            break;
          }
        }
        cx.addElement(cx.elt("MathBlock", from, end < 0 ? cx.prevLineEnd() : end, marks));
        return true;
      },
      endLeaf: (_cx, line) => MATH_FENCE.test(line.text.slice(line.pos)),
    },
  ],
};
